import React from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  ArrowForward as ArrowForwardIcon,
  EmojiEventsOutlined as EmojiEventsOutlinedIcon,
  GroupsOutlined as GroupsOutlinedIcon,
  HomeWorkOutlined as HomeWorkOutlinedIcon,
  VerifiedUserOutlined as VerifiedUserOutlinedIcon,
} from '@mui/icons-material';

const DEFAULT_BACKGROUND_SRC =
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80';

interface FeatureHighlight {
  icon: SvgIconComponent;
  title: string;
  subtitle: string;
}

const FEATURES: FeatureHighlight[] = [
  {
    icon: GroupsOutlinedIcon,
    title: 'Un accompagnement',
    subtitle: 'à chaque étape',
  },
  {
    icon: EmojiEventsOutlinedIcon,
    title: 'Des projets durables',
    subtitle: 'et responsables',
  },
  {
    icon: HomeWorkOutlinedIcon,
    title: 'Une expertise locale',
    subtitle: 'reconnue',
  },
  {
    icon: VerifiedUserOutlinedIcon,
    title: 'La qualité au cœur',
    subtitle: 'de nos engagements',
  },
];

interface ProjectCtaSectionProps {
  onContactClick: () => void;
  backgroundSrc?: string;
  className?: string;
}

const ProjectCtaSection: React.FC<ProjectCtaSectionProps> = ({
  onContactClick,
  backgroundSrc = DEFAULT_BACKGROUND_SRC,
  className = '',
}) => {
  return (
    <section
      className={`project-cta-section relative overflow-hidden ${className}`}
      aria-labelledby="project-cta-heading"
    >
      <img
        src={backgroundSrc}
        alt=""
        className="project-cta-bg absolute inset-0 h-full w-full object-cover object-center"
        loading="lazy"
        decoding="async"
        aria-hidden
      />
      <div className="project-cta-overlay absolute inset-0" aria-hidden />

      <div className="relative z-[1] flex min-h-[min(78vh,720px)] flex-col lg:min-h-[min(82vh,780px)]">
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-16 text-center sm:px-6 sm:py-20 md:py-24">
          <h2
            id="project-cta-heading"
            className="project-cta-heading headline-brand max-w-3xl text-[2rem] sm:text-[2.35rem] lg:text-[2.85rem] xl:text-[3.15rem]"
          >
            Vous avez un projet ?
            <br />
            Parlons-en <span className="headline-brand-accent">ensemble.</span>
          </h2>
          <div className="accent-dash mx-auto mt-6 sm:mt-7" aria-hidden />

          <button
            type="button"
            onClick={onContactClick}
            className="project-cta-button mt-9 inline-flex min-h-[3rem] items-center justify-center gap-2 rounded-md bg-orange-500 px-8 py-3.5 text-[14px] font-medium text-white transition-[background-color,transform,box-shadow] duration-normal hover:bg-orange-600 hover:shadow-[0_8px_28px_rgba(223,130,47,0.35)] active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/50 focus-visible:ring-offset-2 sm:mt-10 sm:px-9 sm:text-[15px]"
          >
            Nous contacter
            <ArrowForwardIcon className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="project-cta-features-bar mt-auto backdrop-blur-[3px]">
          <div className="mx-auto flex w-full max-w-[1440px] flex-col lg:flex-row lg:items-stretch">
            {FEATURES.map(({ icon: Icon, title, subtitle }, index) => (
              <div
                key={title}
                className={`project-cta-feature-cell relative flex flex-1 items-center justify-center gap-3 px-5 py-5 sm:gap-3.5 sm:px-6 sm:py-6 lg:px-7 lg:py-6 xl:px-10 ${
                  index > 0 ? 'project-cta-feature-cell--bordered' : ''
                }`}
              >
                {index > 0 ? (
                  <span className="project-cta-feature-divider hidden lg:block" aria-hidden />
                ) : null}
                <Icon className="h-7 w-7 shrink-0 text-orange-500 lg:h-8 lg:w-8" aria-hidden />
                <div className="min-w-0 text-left">
                  <p className="project-cta-feature-title text-[13px] font-semibold leading-snug sm:text-[14px] lg:text-[15px]">
                    {title}
                  </p>
                  <p className="project-cta-feature-subtitle mt-0.5 text-[12px] font-normal leading-snug sm:text-[13px] lg:text-[14px]">
                    {subtitle}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectCtaSection;
